import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Clock, MessageCircle, Navigation, Building2, ArrowRight } from 'lucide-react';
import { CLINIC, IMAGES } from '../mock';
import { PageHero, SectionTitle, Reveal } from '../components/Shared';

export const LOCATIONS = [
  {
    name: 'Srigyan Rheumatology Clinic',
    type: 'Main Clinic — Ghaziabad',
    hiType: 'मुख्य क्लिनिक — गाज़ियाबाद',
    hiCity: 'गाज़ियाबाद',
    hiHours: 'सोम, गुरु, शुक्र: दोपहर 12:30 – शाम 07:30',
    city: 'Ghaziabad',
    image: IMAGES.waiting,
    address: 'Crossings Republik, Ghaziabad',
    map: 'https://maps.google.com/maps?q=Crossings%20Republik%20Ghaziabad&t=&z=14&ie=UTF8&iwloc=&output=embed',
    hours: 'Mon, Thu, Fri: 12:30 pm – 07:30 pm',
  },
  {
    name: 'Agra Gastro Liver Center',
    type: 'Consultation — Agra',
    hiType: 'परामर्श — आगरा',
    hiCity: 'आगरा',
    city: 'Agra',
    image: IMAGES.examRoom,
    address: 'Agra Gastro Liver Center, Agra, Uttar Pradesh',
    map: 'https://maps.google.com/maps?q=Agra%20Gastro%20Liver%20Center%20Agra&t=&z=13&ie=UTF8&iwloc=&output=embed',
    hours: 'By appointment — please call to confirm',
  },
  {
    name: 'Rudram Hospital',
    type: 'Consultation — Moradabad',
    hiType: 'परामर्श — मुरादाबाद',
    hiCity: 'मुरादाबाद',
    city: 'Moradabad',
    image: IMAGES.consult,
    address: 'Rudram Hospital, Avas Vikas Colony, Moradabad, Uttar Pradesh',
    map: 'https://maps.google.com/maps?q=Rudram%20Hospital%20Avas%20Vikas%20Colony%20Moradabad&t=&z=13&ie=UTF8&iwloc=&output=embed',
    hours: 'By appointment — please call to confirm',
  },
  {
    name: 'Ashok Memorial Sukhsagar Hospital',
    type: 'Consultation — Aligarh',
    hiType: 'परामर्श — अलीगढ़',
    hiCity: 'अलीगढ़',
    city: 'Aligarh',
    image: IMAGES.compassion,
    address: 'Ashok Memorial Sukhsagar Hospital, Aligarh, Uttar Pradesh',
    map: 'https://maps.google.com/maps?q=Ashok%20Memorial%20Sukhsagar%20Hospital%20Aligarh&t=&z=13&ie=UTF8&iwloc=&output=embed',
    hours: 'By appointment — please call to confirm',
  },
];

export default function OurLocations() {
  return (
    <div>
      <PageHero eyebrow="Find Us" title="Our locations" subtitle="Consult Dr Puneet Srivastava at our main clinic in Crossings Republik, Ghaziabad, or at our partner centres in Agra, Moradabad and Aligarh." image={IMAGES.examRoom} />

      <section className="py-20">
        <div className="container-x space-y-14">
          {LOCATIONS.map((l, i) => (
            <Reveal key={l.name}>
              <div className={`grid lg:grid-cols-2 gap-10 items-center ${i % 2 ? 'lg:[direction:rtl]' : ''}`}>
                <div className="[direction:ltr]">
                  <div className="rounded-[2rem] overflow-hidden shadow-2xl">
                    <iframe title={l.name} src={l.map} className="w-full h-[340px] border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
                  </div>
                </div>
                <div className="[direction:ltr]">
                  <span className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-4"><Building2 size={14} /> {l.type}</span>
                  <h2 className="font-display text-2xl md:text-3xl text-teal-900 mb-4" style={{ fontWeight: 600 }}>{l.name}</h2>
                  <ul className="space-y-4">
                    <li className="flex gap-3"><MapPin size={20} className="text-teal-600 shrink-0" /><span className="text-slate-600">{l.address}</span></li>
                    <li className="flex gap-3"><Clock size={20} className="text-teal-600 shrink-0" /><span className="text-slate-600">{l.hours}</span></li>
                    <li className="flex gap-3"><Phone size={20} className="text-teal-600 shrink-0" /><a href={`tel:${CLINIC.phoneRaw}`} className="text-slate-600 hover:text-teal-700">{CLINIC.phone}</a></li>
                  </ul>
                  <div className="flex flex-wrap gap-3 mt-7">
                    <Link to={`/rheumatologist-in/${l.city.toLowerCase()}`} className="btn-teal inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm">Rheumatology in {l.city} <ArrowRight size={16} /></Link>
                    <a href={`https://maps.google.com/maps?q=${encodeURIComponent(l.address)}`} target="_blank" rel="noreferrer" className="btn-outline inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm bg-white"><Navigation size={16} /> Directions</a>
                    <a href={CLINIC.whatsapp} target="_blank" rel="noreferrer" className="btn-outline inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm bg-white"><MessageCircle size={16} /> WhatsApp</a>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mint-section py-16">
        <div className="container-x">
          <div className="bg-white rounded-3xl border border-teal-50 shadow-sm p-8 max-w-3xl mx-auto">
            <div className="flex items-center gap-3 mb-6 justify-center"><span className="grid place-items-center w-11 h-11 rounded-xl bg-teal-50 text-teal-700"><Clock size={20} /></span><h3 className="font-display text-2xl text-teal-900" style={{ fontWeight: 600 }}>Clinic Hours</h3></div>
            <ul className="divide-y divide-teal-50 max-w-md mx-auto">
              {CLINIC.hours.map((h) => (
                <li key={h.d} className="flex items-center justify-between py-3">
                  <span className="text-slate-700">{h.d}</span>
                  <span className="text-sm font-medium" style={h.t === 'Closed' ? { color: 'var(--coral)' } : { color: 'var(--teal-700)' }}>{h.t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
